import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { useLogAnalyticsEvent } from 'shared/src/hooks/useLogAnalyticsEvent';
import { SearchType, SearchTypeLiteral } from 'shared/src/api/enums';
import { searchHelperParamsAtom } from '../states/searchHelperParams.atom';
import { calculateNFDCount } from '../../../utils/nfdCalculator';

type SearchKeywordLogParams = {
  /** SearchType enum 값 */
  searchType: SearchType;
  /** 검색어 (SEARCH_KEYWORD) */
  keyword: string;
  /** 클릭 인덱스 */
  clickIndex?: number;
  /** 사용자 입력 검색어 (자동완성, 직접입력 시) */
  typedKeystroke?: string;
  /** 추천 검색어 개수 (자동완성 시) */
  suggestedWordsCount?: number;
  /** 연령대 그룹 (인기검색어 시) */
  ageGroup?: string;
  /** 추가 analytics 파라미터 (SNO 등) */
  extraAnalytics?: Record<string, unknown>;
};

export const useLogSearchKeyword = () => {
  const params = useAtomValue(searchHelperParamsAtom);
  const isTodaySearch = params.delivery_type === 'today' ? '1' : '0';
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const logSearchKeyword = useCallback(
    ({
      searchType,
      keyword,
      clickIndex,
      typedKeystroke,
      suggestedWordsCount,
      ageGroup,
      extraAnalytics,
    }: SearchKeywordLogParams) => {
      const analytics: Record<string, unknown> = {
        SEARCH_TYPE: SearchTypeLiteral[searchType],
        SEARCH_KEYWORD: keyword,
        IS_TODAY_SEARCH: isTodaySearch,
      };

      // 클릭 인덱스
      if (clickIndex !== undefined) {
        analytics.CLICK_INDEX = clickIndex;
      }

      // 사용자 입력 관련 (자동완성, 직접입력)
      if (typedKeystroke !== undefined) {
        analytics.TYPED_KEYSTROKE = typedKeystroke;
        analytics.TYPED_KEYSTROKE_COUNT = calculateNFDCount(typedKeystroke);
        analytics.SEARCH_KEYWORD_KEYSTROKE_COUNT = calculateNFDCount(keyword);
      }

      // 추천 검색어 개수 (자동완성)
      if (suggestedWordsCount !== undefined) {
        analytics.SUGGESTED_WORDS_COUNT = suggestedWordsCount;
      }

      // 연령대 그룹 (인기검색어)
      if (ageGroup !== undefined) {
        analytics.AGE_GROUP = ageGroup;
      }

      // 추가 파라미터 (SNO 등)
      if (extraAnalytics) {
        Object.assign(analytics, extraAnalytics);
      }

      logAnalyticsEvent({
        name: 'SEARCH_KEYWORD',
        channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
        analytics,
        ably_only_log: {
          event_name: 'keyword_search',
          list_id: searchType.toLowerCase(),
          search_query: keyword,
        },
      });
    },
    [isTodaySearch, logAnalyticsEvent],
  );

  return logSearchKeyword;
};
